import { DatePicker } from '@mui/lab'
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { Refresh as RefreshIcon } from '@mui/icons-material'

import { SparckTable, UserAvatar } from 'components'
import React, { useMemo, useState } from 'react'
import { API } from 'core/requests'
import SnackbarUtils from 'core/SnackbarConfiguration'
import { useUser } from 'contexts'
import { API_ROUTES } from '../../../../core/api'

enum ReportType {
  Object = 'object',
  Array = 'array',
  Tree = 'tree',
}

enum FieldType {
  Integer = 'integer',
}

enum FieldFormat {
  Currency = 'currency',
  Date = 'date',
}

enum CustomRenderType {
  Avatar = 'avatar',
}

enum FilterType {
  Single = 'single',
  Multiple = 'multiple',
  Timeframe = 'timeframe',
  Date = 'date',
}

const getColumnType = column => {
  if ([FieldFormat.Currency, FieldFormat.Date].includes(column.format)) {
    return column.format
  }
  if (column.type === FieldType.Integer) return 'numeric'

  return 'string'
}

const customRenderRowType = column => {
  if (column.name?.includes('_avatar')) return CustomRenderType.Avatar
  return false
}

const customRenderData = (row, column, type) => {
  if (type === CustomRenderType.Avatar) {
    return (
      <UserAvatar
        name={row?.user_name ?? 'User'}
        size={40}
        src={row[column.name]}
      />
    )
  }
}

export default function OrganizationReportingSelectedReport(props) {
  const {
    reportsList,
    selectedReport,
    selectedReportData,
    selectedReportDataIsLoading,
    pagination,
    setPagination,
  } = props

  const selectedReportMeta = useMemo(
    () => selectedReportData?.meta,
    [selectedReportData],
  )

  const selectedReportType = useMemo(
    () => reportsList.find(report => report.key === selectedReport)?.type,
    [reportsList, selectedReport],
  )

  const normalizedSelectedReportData = useMemo(
    () => selectedReportData?.data?.map(row => ({
      ...row,
      parentId: row?.parent_id,
    })) || [],
    [selectedReportData],
  )

  const isBudgetSummaryReport = selectedReport === 'budget-summary-report'

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={1}>
            {normalizedSelectedReportData && !selectedReportDataIsLoading && (
              <>
                <Grid item xs={12} md={6}>
                  <OrganizationReportingReportPicker {...props} />
                </Grid>

                <Grid item xs={12}>
                  <OrganizationReportingFilters {...props} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              {!normalizedSelectedReportData ||
                !selectedReportMeta ||
                selectedReportDataIsLoading ? (
                <Grid container justifyContent='center'>
                  <Grid item xs='auto' sx={{ my: 5 }}>
                    <CircularProgress size={48} />
                  </Grid>
                </Grid>
              ) : (
                <>
                  <SparckTable
                    slug={selectedReport}
                    data={normalizedSelectedReportData}
                    isLoading={selectedReportDataIsLoading}
                    columns={selectedReportMeta?.fields?.map(column => ({
                      title: column.label,
                      field: column.name,
                      type: getColumnType(column),
                      align:
                        column.format === FieldFormat.Currency
                          ? 'right'
                          : 'left',
                      ...(!!customRenderRowType(column) && {
                        render: rowData =>
                          customRenderData(
                            rowData,
                            column,
                            customRenderRowType(column),
                          ),
                      }),
                    }))}
                    {...(selectedReportType === ReportType.Tree && {
                      parentChildData: (row: any, rows) =>
                        rows.find((a: any) => a.id === row.parentId),
                    })}
                    {...(!isBudgetSummaryReport && {
                      pagination,
                      setPagination,
                      totalCount: normalizedSelectedReportData.length,
                    })}
                    style={{ boxShadow: 'none' }}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}

function OrganizationReportingReportPicker({
  reportsList,
  selectedReport,
  setSelectedReport,
}) {
  const { user } = useUser()!
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await API.create('analyticsReportingViewsRefresh', {})
      SnackbarUtils.success('Report data refreshed successfully')
    } catch (error) {
      console.error('Error refreshing report data:', error)
      SnackbarUtils.error('Failed to refresh report data')
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs>
        <TextField
          select
          id='reportsList'
          label='Selected Report'
          value={selectedReport ?? ''}
          onChange={(event: any) => setSelectedReport(event.target.value)}
          variant='outlined'
          margin='normal'
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '1.1em', mb: 0.5 },
          }}
          InputProps={{
            sx: { fontSize: '1.2em', fontWeight: 600 },
          }}
          fullWidth
        >
          {reportsList?.map(reportOption => (
            <MenuItem key={reportOption.key} value={reportOption.key}>
              {reportOption.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {user?.sparck_administrator && (
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRefresh}
            disabled={isRefreshing}
            startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            sx={{ mt: 2 }}
          >
            Refresh Data
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

function OrganizationReportingFilters({
  selectedReportData,
  selectedReportDataIsLoading,
  fetchSelectedReportData,
  selectedReportFilters,
  setSelectedReportFilters,
  selectedReport
}: {
  selectedReportData: any
  selectedReportDataIsLoading: boolean
  fetchSelectedReportData: () => void
  selectedReportFilters: Record<string, any>
  setSelectedReportFilters: (filters: Record<string, any>) => void
  selectedReport: string
}): JSX.Element | null {
  const clearFilters = async () => {
    await setSelectedReportFilters({})
    fetchSelectedReportData()
  };

  const getFilterPicker = (filter: any) => {
    const handleChange = (value: any) => {
      setSelectedReportFilters({
        ...selectedReportFilters,
        [filter.attribute]: value,
      })
    }

    const handleMultipleChange = (event: SelectChangeEvent<any>) => {
      const {
        target: { value },
      } = event

      setSelectedReportFilters({
        ...selectedReportFilters,
        [filter.attribute]:
          typeof value === 'string' ? value.split(',') : value,
      })
    }

    switch (filter.type) {
      case FilterType.Single:
        return (
          <Grid item xs={12} md key={filter.attribute}>
            <TextField
              id={filter.attribute}
              label={filter.label}
              value={selectedReportFilters[filter.attribute] ?? ''}
              onChange={(event) => handleChange(event.target.value)}
              variant='outlined'
              fullWidth
              size='small'
            />
          </Grid>
        )

      case FilterType.Multiple:
        return (
          <Grid item xs={12} md key={filter.attribute}>
            <FormControl fullWidth size='small'>
              <InputLabel id={`${filter.attribute}-label`}>
                {filter.label}
              </InputLabel>
              <Select
                labelId={`${filter.attribute}-label`}
                id={`${filter.attribute}`}
                multiple
                value={selectedReportFilters[filter.attribute] ?? []}
                onChange={handleMultipleChange}
                input={
                  <OutlinedInput label={filter.label} fullWidth size='small' />
                }
                fullWidth
                size='small'
                renderValue={(selected) =>
                  selected
                    .map(
                      (selectedId: any) =>
                        filter.options.find((option: any) => option.id === selectedId)[
                        filter.options_attribute_for_display
                        ],
                    )
                    .join(', ')
                }
              >
                {filter.options.map((option: any) => {
                  const optionLabel = option[filter.options_attribute_for_display];
                  const optionValue = option[filter.options_attribute_for_filter];

                  return (
                    <MenuItem key={optionValue} value={optionValue}>
                      {optionLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        )

      case FilterType.Date:
        return (
          <Grid item xs={12} md key={filter.attribute}>
            <DatePicker
              renderInput={(params) => (
                <TextField
                  {...params}
                  data-cy={filter.attribute}
                  id={filter.attribute}
                  name={filter.attribute}
                  label={filter.label}
                  variant='outlined'
                  size='small'
                />
              )}
              value={selectedReportFilters[filter.attribute] ?? ''}
              onChange={(value) => handleChange(value)}
            />
          </Grid>
        )

      case FilterType.Timeframe:
        return (
          <React.Fragment key={filter.attribute}>
            <Grid item xs={12} md>
              <TextField
                select
                id={filter.attribute}
                label={filter.label}
                value={selectedReportFilters[filter.attribute] ?? ''}
                onChange={(event) => handleChange(event.target.value)}
                variant='outlined'
                size='small'
                fullWidth
              >
                <MenuItem key='' value=''>
                  <em>None</em>
                </MenuItem>

                {filter.options.map((option: any) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {filter.conditional_attributes.map(
              (conditionalAttribute: any, index: number) =>
                selectedReportFilters[filter.attribute] ===
                conditionalAttribute.when_selected_option_is && (
                  <Grid key={index} item xs={12} md>
                    <Grid container spacing={1}>
                      {conditionalAttribute.then_accept_params?.map(
                        (newParams: any) =>
                          getFilterPicker({
                            ...newParams,
                            type: newParams.attribute_format,
                          }),
                      )}
                    </Grid>
                  </Grid>
                ),
            )}
          </React.Fragment>
        )

      default:
        return null
    }
  }

  if (selectedReport !== 'budget-summary-report') {
    return (
      <Grid container spacing={1} alignItems='center'>
        {selectedReportData?.filter?.map(getFilterPicker)}

        <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
          <Grid container spacing={1} justifyContent='flex-end'>
            <Grid item xs={12} sm='auto'>
              <Button variant='outlined' color='primary' onClick={clearFilters}>
                Clear Filters
              </Button>
            </Grid>

            <Grid item xs={12} sm='auto'>
              <Button
                variant='contained'
                color='primary'
                onClick={fetchSelectedReportData}
                disabled={selectedReportDataIsLoading}
              >
                {selectedReportDataIsLoading && <CircularProgress size={24} />}
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return null
}